import {measureSummaryBold, SUMMARY_BOLD_SPAN} from './learnBoldUtils'

/**
 * Measures how much of the user's own note content survived a USER_GUIDED ("Finish notes") merge,
 * and how much of what survived carries provenance bold.
 *
 * Pure and deterministic: no LLM call, so the same function runs as per-job production telemetry
 * and as the scorer for the offline eval fixtures. A metric that disagrees with the eval gating it
 * is worse than no metric, so there is deliberately only one implementation.
 *
 * `recall` is a LOWER BOUND. Matching is content-word overlap against a local window, so a point
 * the model rewrote heavily ("ship by Q3" into "the team committed to a third-quarter delivery")
 * scores as absent. Watch trends and compare variants with it; do not treat a single job's number
 * as ground truth.
 *
 * Measured accuracy, from calibration against a hand-labelled sample (48 bold spans and 48
 * line/run pairs drawn from real merges of the three hour-scale fixtures): span crediting is
 * wrong ~8% of labels in each direction; point presence over-credits ~6% and under-credits ~12%.
 * Every residual presence miss is a semantic rephrasing no lexical matcher can see, so a
 * `present: false` means "possibly missing", while `present: true` is reliable. The labelled
 * sample is committed at services/llm/src/tests/data/finish-notes-calibration/;
 * after changing any threshold or matching rule here, regenerate its answer keys from the same
 * run outputs, re-run `bun run finish-notes-calibration-report` (services/llm) against it, and
 * bump PROVENANCE_SCORER_VERSION so the emitted series can be split at the deploy.
 */

// A point needs at least this many distinct content words to be matchable. Below it, overlap
// scoring is noise: a two-word fragment matches almost any bullet on the same topic.
const MIN_POINT_CONTENT_WORDS = 3

// A bold span needs at least this many content words before overlap can credit it to the user. The
// same noise argument as MIN_POINT_CONTENT_WORDS, on the other side of the comparison: a one-word
// span clears any overlap threshold as soon as that word appears in any note line, so a wall of
// model key terms drawn from the user's own subject matter scores as the user's writing — which is
// precisely the "lots of bold, none of it mine" case boldPrecision exists to detect. Spans below the
// floor stay in the denominator; they are bold the reader sees, just not evidence of authorship.
const MIN_JUDGEABLE_SPAN_CONTENT_WORDS = 2

// Fraction of a point's content words that must appear inside one output window for it to count as
// present. Calibrated against a hand-labelled sample (48 line/run pairs across the three
// hour-scale fixtures): at 0.6 the scorer called 18 of the 24 lines it rejected missing when a
// human could find them rephrased in the output, because the merge expands abbreviations and
// rewords more than exact word overlap tolerates. 0.45 is the floor: at 0.4, a two-word fragment
// of a five-word point counts as survival, and a wall of key terms lifted from the notes reads
// as the notes surviving via adjacent-pair windows — both cases a human calls lost.
const POINT_PRESENT_OVERLAP_THRESHOLD = 0.45

// Fraction of a bold span's content words that must appear in one of the user's note lines before
// the span is credited to the user. Deliberately stricter than the point threshold: spans are
// short, so loose overlap credits model key terms drawn from the user's own vocabulary — the
// "lots of bold, none of it mine" case boldPrecision exists to detect. The same hand-labelled
// sample (48 spans) puts the best trade-off at 0.55: 4 over-credits and 4 under-credits, versus
// 4 and 5 at 0.6 and 7 over-credits at 0.4.
const SPAN_USER_OVERLAP_THRESHOLD = 0.55

// Names the calibration generation that produced a number, and travels with every emitted metric.
// Recall, markedRate and boldPrecision all step when a threshold, a matching rule, or what content
// they are scored over changes — a calibration moved recall from 0.44-0.75 to 0.81-1.00 on the same
// fixtures — and on a dashboard that step is indistinguishable from a real change in merge
// quality. Splitting a series by this is the only way to compare like with like across the deploy.
//
// Bump it in the same commit as any change to a threshold, a matching rule, or a cap above, or to
// what content a ratio is scored over; version 1 is the pre-calibration scorer, which never emitted
// this.
export const PROVENANCE_SCORER_VERSION = 4

// Scoring costs (total content words) x (output windows) and runs synchronously inside summary
// post-processing, on a service running 60 concurrent jobs on one event loop. A slow measurement
// does not just delay itself, it stalls every other user's summary and SSE stream.
//
// Counting points is not enough to bound that. splitUserPoints breaks on newlines and sentence
// ends, so a single line with no full stop is ONE point however long it is: a 5 MB paste (the
// express body limit, and there is no length validation before here) measured at 9.4 seconds of
// blocking while pointCount stayed at 1. The word budget is what actually bounds the work; the
// point and window caps stay as cheap guards on the other two dimensions.
//
// Anyone raising one of these should measure rather than assume headroom. Both matching
// directions are inverted indexes (match key -> postings), so cost is keys plus the postings
// actually hit: re-measured at the caps with all-miss content, the windows term is ~15 ms at 1900
// points x ~1200 windows, the bold term ~18 ms at 2000 spans, ~20 ms combined. The postings
// degenerate toward the cross product when the note's lines repeat (a re-merge note is prior
// output): the same caps over 1900 near-identical points measured ~300 ms before the full-match
// early exit in scoreBoldSpans, which restores the common fully-quoted shape to O(span words).
// (The naive per-(key, window) scans measured over 1s for the fuzzy side and ~150 ms for the exact
// side at the same caps — do not "simplify" back to them without re-measuring.)
//
// Sized against observed output rather than against what the model could emit in theory. The
// hour-scale eval fixtures and their real merges top out near 370 output windows and 151 bold spans;
// the caps sit roughly 3x and 13x above that, which leaves an unusual-but-real merge scorable
// while keeping the worst case well inside the low hundreds of ms.
const MAX_SCORABLE_POINTS = 2_000
const MAX_SCORABLE_OUTPUT_WINDOWS = 1_200
const MAX_SCORABLE_CONTENT_WORDS = 10_000
// The window cap does not bound this: spans-per-line is unbounded, so a wall of short key terms
// packed into a few lines passes the window cap untouched. resolveSummaryBold's length
// fallback keeps spans of <= 6 words, so a bold-saturated merge capped that way is exactly the
// shape that reaches here intact.
const MAX_SCORABLE_BOLD_SPANS = 2_000

// The word budget bounds the matching, but not tokenizing one oversized point: contentWords()
// lowercases and scans a whole line before the budget is consulted, so a 5 MB single-line paste
// still costs ~130 ms. This char check is O(1) and makes the worst case flat. Three orders of
// magnitude above a real note, which tops out in the low thousands of characters.
const MAX_SCORABLE_INPUT_CHARS = 400_000

const WORD_PATTERN = /[\p{L}\p{N}]+/gu

// Scripts that do not delimit words with whitespace. Token-overlap scoring is meaningless there, so
// the measurement is refused rather than silently wrong.
const UNSEGMENTED_SCRIPT_PATTERN =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Script=Thai}]/u

// Leading markdown syntax on a line: heading hashes, bullet and checkbox markers, ordered-list
// markers, blockquote markers. Stripped so list punctuation never counts as content.
const LINE_MARKUP_PATTERN = /^\s*(?:#{1,6}\s+|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+|>\s*)/

// Function words carry no provenance signal; keeping them would let any two English sentences score
// as overlapping. English-only on purpose: other Latin-script languages still score, just with more
// noise, and unsegmented scripts are refused outright above.
const STOPWORDS = new Set([
  'a',
  'the',
  'and',
  'or',
  'but',
  'if',
  'then',
  'than',
  'that',
  'this',
  'these',
  'those',
  'it',
  'its',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'am',
  'do',
  'does',
  'did',
  'doing',
  'have',
  'has',
  'had',
  'having',
  'will',
  'would',
  'shall',
  'should',
  'can',
  'could',
  'may',
  'might',
  'must',
  'to',
  'of',
  'in',
  'on',
  'at',
  'by',
  'for',
  'with',
  'from',
  'into',
  'onto',
  'over',
  'under',
  'about',
  'as',
  'so',
  'not',
  'no',
  'nor',
  'we',
  'our',
  'us',
  'you',
  'your',
  'they',
  'their',
  'them',
  'he',
  'she',
  'his',
  'her',
  'i',
  'my',
  'me',
  'there',
  'here',
  'what',
  'which',
  'who',
  'whom',
  'when',
  'where',
  'why',
  'how',
  'all',
  'any',
  'both',
  'each',
  'few',
  'more',
  'most',
  'other',
  'some',
  'such',
  'only',
  'own',
  'same',
  'very',
  'just',
  'also',
  'too',
  'one',
  'up',
  'down',
  'out',
  'off'
])

export type ProvenancePoint = {
  /** The user's own line or sentence, as written. */
  text: string
  /** Its content appears in the merge output. */
  present: boolean
  /** It is present AND its content sits inside a `**...**` span. */
  marked: boolean
  /**
   * Best stem-overlap fraction over the qualifying output windows, in [0, 1]. Useful for triaging
   * near-misses, with one blind spot: a pair window sharing fewer than MIN_PAIR_WINDOW_HITS words
   * contributes 0 rather than its raw fraction, so a near-miss that lives only in a pair window
   * reads lower than the words on screen suggest.
   */
  bestOverlap: number
}

export type ProvenanceBoldSpan = {
  /** The span's text without the `**` markers. */
  text: string
  /** Counted in boldPrecision's denominator (has at least one content word). */
  judgeable: boolean
  /** The span's content came from the user's notes. */
  userDerived: boolean
  /** Best exact-word overlap against any single scorable user point, divided by the SPAN's word count. */
  bestOverlap: number
}

export type ProvenanceMeasurement = {
  /**
   * False when the measurement cannot be trusted: content in a script without whitespace word
   * boundaries, or no point long enough to match. Callers must drop unscorable measurements rather
   * than aggregate their zeros, which would read identically to total content loss.
   */
  scorable: boolean
  /** Per-point detail, for local debugging. Carries the user's own text: never emit it. */
  points: ProvenancePoint[]
  /** Per-span detail behind boldPrecision, for local debugging. Carries output text: never emit it. */
  boldSpans: ProvenanceBoldSpan[]
  /** How many of the user's lines were long enough to score. */
  pointCount: number
  /** How many of those were found in the output. Given alongside `recall` so a ratio can be read in context: 0.6 means something different at 5 points than at 50. */
  survivedCount: number
  /** Fraction of the user's points present in the output, in [0, 1]. 0 when unscorable. */
  recall: number
  /**
   * Fraction of the PRESENT points carrying provenance bold, in [0, 1].
   *
   * Null when nothing survived, because 0/0 is undefined and a 0 there would read as "the user's
   * content survived but went unmarked", which is a different and much less alarming failure.
   */
  markedRate: number | null
  /** Total `**...**` spans in the output. */
  boldSpanCount: number
  /**
   * Fraction of the output's JUDGEABLE bold spans that correspond to something the user wrote, in
   * [0, 1]. Judgeable excludes spans with no content word at all (bold on pure stopwords), so this
   * does not compose with `boldSpanCount` — that is every span, this divides by a subset.
   *
   * This is the one that matches the reported complaint, "I have a lot of bold but none of it is my
   * text". `markedRate` asks whether the user's points got marked; a merge can score 1.0 there and
   * still bury those marks under a hundred key-term spans. This asks the reader's question instead:
   * of the bold on screen, how much of it is mine. 0 when there are no bold spans, or unscorable.
   */
  boldPrecision: number
}

/**
 * The telemetry-safe projection: counts and ratios, no text. Emission reads this rather than the
 * measurement, so no content can reach a span attribute by someone forgetting the rule.
 *
 * The type alone does NOT enforce that: callers spread this into a wider object, and TypeScript
 * exempts spread-in properties from excess-property checking, so a `points` field added here would
 * compile. The guard that actually holds is the exact-key assertion in learnProvenance.spec.ts,
 * plus `setValidateResponseAttributes` naming every attribute it emits. Keep both.
 */
export type ProvenanceTelemetry = {
  scorable: boolean
  pointCount: number
  survivedCount: number
  recall: number
  markedRate: number | null
  boldPrecision: number
  /** Which calibration generation produced these numbers (see PROVENANCE_SCORER_VERSION). */
  scorerVersion: number
}

export function toProvenanceTelemetry(measurement: ProvenanceMeasurement): ProvenanceTelemetry {
  const {scorable, pointCount, survivedCount, recall, markedRate, boldPrecision} = measurement
  return {
    scorable,
    pointCount,
    survivedCount,
    recall,
    markedRate,
    boldPrecision,
    scorerVersion: PROVENANCE_SCORER_VERSION
  }
}

// A function, not a shared constant: `points` is a mutable array on an exported return type, and
// one caller sorting or pushing to it would corrupt every later unscorable measurement in the
// process.
const unscorable = (): ProvenanceMeasurement => ({
  scorable: false,
  points: [],
  boldSpans: [],
  pointCount: 0,
  survivedCount: 0,
  recall: 0,
  markedRate: null,
  boldSpanCount: 0,
  boldPrecision: 0
})

/**
 * Share of the note's tokens that are English function words. The span rule is English-shaped
 * through STOPWORDS, and outside English it errs both ways — Spanish function-word spans score as
 * the user's writing, inflected German restatements score as the model's. The strip ships
 * language-agnostic by decision, so this rides on telemetry to make the size of that exposure
 * knowable before anyone decides whether a gate is warranted.
 */
export function lexicalConfidence(userNoteText: string): number {
  const words = userNoteText.toLowerCase().match(WORD_PATTERN) ?? []
  if (words.length === 0) return 0
  return words.filter((word) => STOPWORDS.has(word)).length / words.length
}

function contentWords(text: string): Set<string> {
  const words = text.toLowerCase().match(WORD_PATTERN) ?? []
  return new Set(words.filter((word) => !STOPWORDS.has(word)))
}

/**
 * Splits the user's notes into the points checked for survival: one per line, with long lines broken
 * at sentence ends. Notes are usually written as short lines, so a line is the natural "point"; a
 * pasted paragraph still gets split rather than counting as one all-or-nothing point.
 *
 * Markdown headings the user wrote are dropped. The merge reorganises content under its own
 * headings and does not bold section titles, so scoring a user's heading would count a miss the
 * feature was never trying to achieve and drag both recall and markedRate down.
 */
function splitUserPoints(userNoteText: string): string[] {
  return userNoteText
    .split('\n')
    .filter((line) => !/^\s*#{1,6}\s/.test(line))
    .flatMap((line) => line.replace(LINE_MARKUP_PATTERN, '').split(/(?<=[.!?])\s+/))
    .map((point) => point.trim())
    .filter((point) => point.length > 0)
}

// A pair window only counts when it shares at least this many words with the point. Pair windows
// exist for a point the model split across a bullet and its detail, and a genuinely split point
// shares several words with the pair; two shared words in adjacent lines is the coincidence shape
// of a key-term wall, which at the calibrated point threshold would otherwise read as survival for
// a short point.
const MIN_PAIR_WINDOW_HITS = 3

/**
 * Splits the merge output into the single-line windows a point is matched against. Locality is the
 * whole point: across a full 2000-word summary every point's words appear *somewhere*, so a global
 * match would report ~100% recall even on output that dropped the user's notes entirely. Adjacent
 * line PAIRS are also matched, because the model often splits one user point across a bullet and
 * its detail — a pair's word set is the union of its two lines' sets (tokens cannot merge across
 * the line boundary), and pairs carry a minimum-hits floor single lines don't need (see
 * MIN_PAIR_WINDOW_HITS).
 */
function splitOutputLines(summaryMarkdown: string): string[] {
  return summaryMarkdown
    .split('\n')
    .map((line) => line.replace(LINE_MARKUP_PATTERN, '').trim())
    .filter((line) => line.length > 0)
}

function pairwiseUnions(lineWordSets: Set<string>[]): Set<string>[] {
  return lineWordSets.slice(0, -1).map((words, index) => {
    const union = new Set(words)
    for (const word of lineWordSets[index + 1] ?? []) {
      union.add(word)
    }
    return union
  })
}

// ---- Point-side fuzzy matching ----------------------------------------------------------------
//
// Point-presence comparisons tolerate token variation; span crediting stays exact. The merge
// inflects and extends the user's tokens ("lands" -> "landed", "deploy" -> "deployment", "9:47"
// tokenizing differently from "9:47am"), and under exact equality every varied token stops
// counting toward the point's survival — the dominant error in the hand-labelled calibration.
// The span side is NOT loosened: it is already balanced, and fuzzier matching there would credit
// more model key terms as the user's.
//
// A point word matches a window word when any of:
//   - their light stems are equal (trailing ings/ing/ed/es/s stripped, then a trailing "e", while
//     >=3 chars remain — the "e" strip is what makes "note"/"notes" and "case"/"cases" meet, and
//     "ings" before "ing" is what makes "meetings"/"meeting" meet at "meet"), or
//   - one stem is a prefix of the other and the shorter has >= MIN_STEM_PREFIX_CHARS chars — five,
//     not four, because at four "cell" matches "cellular" and flips a key-term-shaped point to
//     present on coincidence (the wall test below guards exactly that), at the cost of a 4-char
//     abbreviation like "prev" not matching its expansion. All-digit stems are excluded on BOTH
//     sides: "20241" is not "202412", and excluding one side only would make that pair match or
//     not depending on which of the two the user wrote, or
//   - one token is all digits (>= MIN_DIGIT_PREFIX_CHARS) and the other is those digits plus a
//     non-digit remainder — the shape WORD_PATTERN makes of clock times and unit suffixes,
//     "9:47am" tokenizing as "9" + "47am" while the user's "9:47" tokenizes as "9" + "47".
const MIN_STEM_PREFIX_CHARS = 5
const MIN_DIGIT_PREFIX_CHARS = 2

function stemToken(word: string): string {
  const stripped = word.replace(/(?:ings|ing|ed|es|s)$/, '')
  const trimmed = stripped.endsWith('e') ? stripped.slice(0, -1) : stripped
  if (trimmed.length >= 3) return trimmed
  if (stripped.length >= 3) return stripped
  return word
}

const ALL_DIGITS_PATTERN = /^\d+$/
const LEADING_DIGITS_PATTERN = /^\d+/

// The point side of the same precomputation: everything derivable from the stem alone, built once
// per point rather than once per (stem, window) comparison.
type StemMatchFeatures = {
  stem: string
  /** True when the whole stem is a usable forward digit key (all digits, >= MIN_DIGIT_PREFIX_CHARS). */
  isDigitKey: boolean
  /** True when the stem may consult strictPrefixes (long enough to be a key there, and not all digits). */
  isPrefixKey: boolean
  /**
   * Reverse-prefix candidates: every strict leading slice of the stem long enough to be a match
   * key. Empty for an all-digit stem, on the same terms as `isPrefixKey` — the prefix rule reaches
   * the index through both, so excluding digits from only one makes the rule direction-dependent.
   */
  prefixes: string[]
  /** Leading digit run when it is a usable digit-rule key ("47am" -> "47"), else null. */
  digitRun: string | null
}

function buildStemFeatures(stem: string): StemMatchFeatures {
  const isDigits = ALL_DIGITS_PATTERN.test(stem)
  const prefixes: string[] = []
  if (!isDigits) {
    for (let length = MIN_STEM_PREFIX_CHARS; length < stem.length; length++) {
      prefixes.push(stem.slice(0, length))
    }
  }
  const digitRun = stem.match(LEADING_DIGITS_PATTERN)?.[0]
  return {
    stem,
    isDigitKey: isDigits && stem.length >= MIN_DIGIT_PREFIX_CHARS,
    isPrefixKey: !isDigits && stem.length >= MIN_STEM_PREFIX_CHARS,
    prefixes,
    digitRun:
      !isDigits && digitRun !== undefined && digitRun.length >= MIN_DIGIT_PREFIX_CHARS && digitRun.length < stem.length
        ? digitRun
        : null
  }
}

/**
 * Point-side counterpart of scoreBoldSpans' exact crediting: fuzzy token matching. A WINDOW here is
 * whatever the point is scored against, which depends on the caller: one output line, one
 * adjacent-line pair, or one bold span.
 *
 * A point stem credits a window when: the stems are equal; the window stem strictly extends it
 * (strict-prefix postings); the window stem is the point stem's digit run plus a non-digit
 * remainder (digit postings); or a strict prefix / digit run of the point stem equals a window
 * stem (reverse lookups against the stem postings). Each window is credited at most once per stem.
 */
function buildFuzzyMatcher(
  windowWordSets: Set<string>[]
): (pointStems: StemMatchFeatures[], minHits: number) => {overlap: number; windowIndex: number} {
  const windowCount = windowWordSets.length
  const windowsByStem = new Map<string, number[]>()
  const windowsByStrictPrefix = new Map<string, number[]>()
  const windowsByDigitKey = new Map<string, number[]>()
  const addPosting = (postingsByKey: Map<string, number[]>, matchKey: string, windowIndex: number) => {
    const postings = postingsByKey.get(matchKey)
    if (postings === undefined) postingsByKey.set(matchKey, [windowIndex])
    // A window's words are posted in one pass, so a duplicate key from the same window is last.
    else if (postings[postings.length - 1] !== windowIndex) postings.push(windowIndex)
  }
  for (let windowIndex = 0; windowIndex < windowCount; windowIndex++) {
    const words = windowWordSets[windowIndex]
    if (words === undefined) continue
    for (const word of words) {
      // Both sides of a comparison derive their match keys from the same buildStemFeatures, so
      // the key rules have exactly one encoding.
      const features = buildStemFeatures(stemToken(word))
      addPosting(windowsByStem, features.stem, windowIndex)
      for (const prefix of features.prefixes) {
        addPosting(windowsByStrictPrefix, prefix, windowIndex)
      }
      if (features.digitRun !== null) {
        addPosting(windowsByDigitKey, features.digitRun, windowIndex)
      }
    }
  }

  // Scratch state reused across calls: creditedStemByWindow dedups the several posting lists one
  // stem can hit. Its mark climbs across calls rather than restarting per call, so a mark left by
  // an earlier call can never read as this call's stem and cost that window its credit.
  const hitsByWindow = new Int32Array(windowCount)
  const creditedStemByWindow = new Int32Array(windowCount).fill(-1)
  const windowsWithHits: number[] = []
  let currentStemMark = -1
  const creditWindows = (postings: number[] | undefined) => {
    if (postings === undefined) return
    for (const windowIndex of postings) {
      if (creditedStemByWindow[windowIndex] === currentStemMark) continue
      creditedStemByWindow[windowIndex] = currentStemMark
      if (hitsByWindow[windowIndex] === 0) windowsWithHits.push(windowIndex)
      hitsByWindow[windowIndex] = (hitsByWindow[windowIndex] ?? 0) + 1
    }
  }

  return (pointStems, minHits) => {
    if (pointStems.length === 0 || windowCount === 0) return {overlap: 0, windowIndex: -1}
    for (const windowIndex of windowsWithHits) {
      hitsByWindow[windowIndex] = 0
    }
    windowsWithHits.length = 0
    for (const features of pointStems) {
      currentStemMark++
      creditWindows(windowsByStem.get(features.stem))
      if (features.isPrefixKey) creditWindows(windowsByStrictPrefix.get(features.stem))
      if (features.isDigitKey) creditWindows(windowsByDigitKey.get(features.stem))
      for (const prefix of features.prefixes) {
        creditWindows(windowsByStem.get(prefix))
      }
      if (features.digitRun !== null) creditWindows(windowsByStem.get(features.digitRun))
    }
    // The winning window rides along with the score: the bold strip needs to know WHICH span marks
    // a point so it can put that one span back. `windowsWithHits` is walked in a fixed order for a
    // given input, so ties resolve to the same window on every run.
    let bestOverlap = 0
    let bestWindowIndex = -1
    for (const windowIndex of windowsWithHits) {
      const hits = hitsByWindow[windowIndex]
      if (hits === undefined || hits < minHits) continue
      const overlap = hits / pointStems.length
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestWindowIndex = windowIndex
      }
    }
    return {overlap: bestOverlap, windowIndex: bestWindowIndex}
  }
}

/**
 * Scores every bold span against the user's note lines and derives boldPrecision. Note the
 * direction: overlap divides by the SPAN's word count, asking "is this span mine", where the
 * point-side fuzzy matcher divides by the POINT's count, asking "did my point survive". A key term
 * lifted from the transcript scores near 0 here even when it sits in a bullet that also carries
 * the user's words. Crediting is EXACT word equality on purpose (see the fuzzy-matcher comment).
 */
function scoreBoldSpans(
  boldSpanWords: {text: string; words: Set<string>}[],
  pointWordSets: Set<string>[]
): {spans: ProvenanceBoldSpan[]; boldPrecision: number} {
  const pointsByWord = new Map<string, number[]>()
  for (let pointIndex = 0; pointIndex < pointWordSets.length; pointIndex++) {
    const words = pointWordSets[pointIndex]
    if (words === undefined) continue
    for (const word of words) {
      const postings = pointsByWord.get(word)
      if (postings === undefined) pointsByWord.set(word, [pointIndex])
      else postings.push(pointIndex)
    }
  }

  const hitsByPoint = new Int32Array(pointWordSets.length)
  const pointsWithHits: number[] = []
  const scoredSpans: ProvenanceBoldSpan[] = []
  let judgeableCount = 0
  let userDerivedCount = 0
  for (const boldSpan of boldSpanWords) {
    const text = boldSpan.text.replace(/^\*\*|\*\*$/g, '')
    if (boldSpan.words.size === 0) {
      scoredSpans.push({text, judgeable: false, userDerived: false, bestOverlap: 0})
      continue
    }
    for (const pointIndex of pointsWithHits) {
      hitsByPoint[pointIndex] = 0
    }
    pointsWithHits.length = 0
    let bestHits = 0
    for (const word of boldSpan.words) {
      const postings = pointsByWord.get(word)
      if (postings === undefined) continue
      for (const pointIndex of postings) {
        if (hitsByPoint[pointIndex] === 0) pointsWithHits.push(pointIndex)
        const hits = (hitsByPoint[pointIndex] ?? 0) + 1
        hitsByPoint[pointIndex] = hits
        if (hits > bestHits) bestHits = hits
      }
      // A full match cannot be beaten, and the fully-quoted span is a common shape (a re-merge note
      // IS prior output); leaving hitsByPoint dirty is fine, the next span clears via pointsWithHits.
      if (bestHits === boldSpan.words.size) break
    }
    const overlap = bestHits / boldSpan.words.size
    const userDerived =
      boldSpan.words.size >= MIN_JUDGEABLE_SPAN_CONTENT_WORDS && overlap >= SPAN_USER_OVERLAP_THRESHOLD
    judgeableCount++
    if (userDerived) userDerivedCount++
    scoredSpans.push({text, judgeable: true, userDerived, bestOverlap: overlap})
  }
  return {spans: scoredSpans, boldPrecision: judgeableCount === 0 ? 0 : userDerivedCount / judgeableCount}
}

type CandidatePoint = {text: string; words: Set<string>; stems: StemMatchFeatures[]}

/**
 * The user's matchable points, or null when they cannot be trusted: input over the size caps, an
 * unsegmented script, or no point long enough to match. Shared by measureProvenance and the
 * saturation cap's scoreSpansAgainstNotes, so a span is judged against the same points either way.
 *
 * Built with a running budget rather than mapped-then-filtered, so an oversized note bails part
 * way instead of allocating a word Set for every point first and only then discovering it is too
 * big. The check has to be inside the loop: one 5 MB line is a single point, so any cap tested
 * afterwards has already paid the full cost.
 */
function scorableUserPoints(userNoteText: string): CandidatePoint[] | null {
  if (userNoteText.length > MAX_SCORABLE_INPUT_CHARS || UNSEGMENTED_SCRIPT_PATTERN.test(userNoteText)) {
    return null
  }
  const candidatePoints: CandidatePoint[] = []
  let totalContentWords = 0
  for (const text of splitUserPoints(userNoteText)) {
    const words = contentWords(text)
    totalContentWords += words.size
    if (totalContentWords > MAX_SCORABLE_CONTENT_WORDS) {
      return null
    }
    if (words.size < MIN_POINT_CONTENT_WORDS) {
      continue
    }
    // The matchability floor is enforced on distinct STEMS, the denominator the point is actually
    // scored with: morphological variants collapse under stemming, and a point reduced to two
    // stems is the two-word fragment the floor exists to exclude.
    const stems = [...new Set([...words].map(stemToken))].map(buildStemFeatures)
    if (stems.length >= MIN_POINT_CONTENT_WORDS) {
      candidatePoints.push({text, words, stems})
      if (candidatePoints.length > MAX_SCORABLE_POINTS) {
        return null
      }
    }
  }
  return candidatePoints.length === 0 ? null : candidatePoints
}

export function measureProvenance(userNoteText: string, summaryMarkdown: string): ProvenanceMeasurement {
  if (summaryMarkdown.length > MAX_SCORABLE_INPUT_CHARS || UNSEGMENTED_SCRIPT_PATTERN.test(summaryMarkdown)) {
    return unscorable()
  }
  const candidatePoints = scorableUserPoints(userNoteText)
  if (candidatePoints === null) {
    return unscorable()
  }

  const outputLines = splitOutputLines(summaryMarkdown)
  if (outputLines.length + Math.max(0, outputLines.length - 1) > MAX_SCORABLE_OUTPUT_WINDOWS) {
    return unscorable()
  }

  // Shares learnBoldUtils' span extraction rather than repeating the pattern: the saturation cap
  // and this metric run over the same content, so a change to what counts as a bold span has to
  // move both at once or markedRate stops describing the bold the user was left with.
  const boldSpanTexts = measureSummaryBold(summaryMarkdown).spans
  if (boldSpanTexts.length > MAX_SCORABLE_BOLD_SPANS) {
    return unscorable()
  }

  const lineWordSets = outputLines.map(contentWords)
  const lineOverlap = buildFuzzyMatcher(lineWordSets)
  const pairOverlap = buildFuzzyMatcher(pairwiseUnions(lineWordSets))
  const boldSpanWords = boldSpanTexts.map((text) => ({text, words: contentWords(text)}))
  const boldOverlap = buildFuzzyMatcher(boldSpanWords.map((span) => span.words))

  const points = candidatePoints.map<ProvenancePoint>((point) => {
    const overlap = Math.max(
      lineOverlap(point.stems, 1).overlap,
      pairOverlap(point.stems, MIN_PAIR_WINDOW_HITS).overlap
    )
    const present = overlap >= POINT_PRESENT_OVERLAP_THRESHOLD
    return {
      text: point.text,
      present,
      marked: present && boldOverlap(point.stems, 1).overlap >= POINT_PRESENT_OVERLAP_THRESHOLD,
      bestOverlap: overlap
    }
  })

  const presentPoints = points.filter((point) => point.present)
  const {spans: scoredBoldSpans, boldPrecision} = scoreBoldSpans(
    boldSpanWords,
    candidatePoints.map((point) => point.words)
  )
  return {
    scorable: true,
    points,
    boldSpans: scoredBoldSpans,
    pointCount: points.length,
    survivedCount: presentPoints.length,
    recall: presentPoints.length / points.length,
    markedRate: presentPoints.length === 0 ? null : presentPoints.filter((p) => p.marked).length / presentPoints.length,
    boldSpanCount: boldSpanWords.length,
    boldPrecision
  }
}

// The cap's view of the scorer: the same user points and span rule boldPrecision uses, with
// measureProvenance's guards restricted to what a span list can be judged on. Null when
// classification cannot be trusted (unsegmented script, size caps, notes with no point long enough
// to match); callers must fall back rather than treat it as "none are the user's". The span-side
// size caps are the one input that shrinks when the cap runs, so a second pass over content the
// length rule capped for size can classify where the first could not.
function scoreSpansAgainstNotes(
  userNoteText: string,
  spans: string[]
): {spans: ProvenanceBoldSpan[]; boldPrecision: number; points: CandidatePoint[]; spanWords: Set<string>[]} | null {
  if (spans.length > MAX_SCORABLE_BOLD_SPANS) {
    return null
  }
  // The span count does not bound tokenizing one enormous span, so the character total is capped
  // too — same reasoning as MAX_SCORABLE_INPUT_CHARS on the note side.
  if (spans.reduce((sum, span) => sum + span.length, 0) > MAX_SCORABLE_INPUT_CHARS) {
    return null
  }
  if (spans.some((span) => UNSEGMENTED_SCRIPT_PATTERN.test(span))) {
    return null
  }
  const points = scorableUserPoints(userNoteText)
  if (points === null) {
    return null
  }
  const boldUnits = spans.map((text) => ({text, words: contentWords(text)}))
  // The points and the span word sets ride back out: the rescue pass needs both, and rebuilding
  // them there would re-split the note and re-tokenize every span for a second time.
  return {
    ...scoreBoldSpans(
      boldUnits,
      points.map((point) => point.words)
    ),
    points,
    spanWords: boldUnits.map((unit) => unit.words)
  }
}

/**
 * Span indices that must keep their bold so no surviving user point is left unmarked. Judged by the
 * same fuzzy rule `marked` uses, against the bold spans alone: a point whose words sit inside a
 * bold span is present by construction, so the line and pair matchers are not needed and the strip
 * stays well inside its cost budget.
 */
function spansRescuingOrphanedPoints(
  points: CandidatePoint[],
  spanWords: Set<string>[],
  kept: Set<number>
): Set<number> {
  // Split rather than search the whole set: the matcher reports only its single best window, so one
  // pass cannot tell "no kept span covers this point" from "a stripped span covers it better", and
  // rescuing on the latter adds back bold the strip was right to remove. Every span lands in exactly
  // one of the two indexes, so this builds no more posting lists than a single matcher did.
  const keptWords: Set<string>[] = []
  const strippedWords: Set<string>[] = []
  const strippedSpanIndices: number[] = []
  spanWords.forEach((words, index) => {
    if (kept.has(index)) {
      keptWords.push(words)
      return
    }
    strippedWords.push(words)
    strippedSpanIndices.push(index)
  })
  const keptOverlap = buildFuzzyMatcher(keptWords)
  const strippedOverlap = buildFuzzyMatcher(strippedWords)

  const rescued = new Set<number>()
  for (const point of points) {
    if (keptOverlap(point.stems, 1).overlap >= POINT_PRESENT_OVERLAP_THRESHOLD) continue
    const best = strippedOverlap(point.stems, 1)
    if (best.overlap < POINT_PRESENT_OVERLAP_THRESHOLD) continue
    // A -1 window index falls out here as undefined, so no separate guard for "nothing matched".
    const spanIndex = strippedSpanIndices[best.windowIndex]
    if (spanIndex !== undefined) rescued.add(spanIndex)
  }
  return rescued
}

// When more than this fraction of the summary's characters is bold — typically because the note
// body is itself the output of a previous merge — the marking no longer distinguishes anything
// and the summary reads as a solid bold wall, so resolveSummaryBold strips it back.
export const SUMMARY_BOLD_SATURATION_RATIO = 0.5
// In the length fallback, bold spans of at most this many words keep their bold (they read as
// key-term emphasis); longer spans are unwrapped to plain text.
export const SUMMARY_BOLD_KEEP_MAX_WORDS = 6

// Which rule decided what bold to keep. 'provenance' keeps the user's spans and unwraps the
// model's; 'length' is the fallback when spans cannot be classified (no user text, unsegmented
// script, size caps); 'none' means the content was under the threshold and untouched.
export type SummaryBoldCapMode = 'none' | 'provenance' | 'length'

export type SummaryBoldResult = {
  content: string
  capMode: SummaryBoldCapMode
  // Whether the winning rule unwrapped anything: a provenance pass that keeps every span crosses
  // the threshold yet leaves the content as it was. Telemetry on both sides reports this one value.
  contentChanged: boolean
  preCapBoldRatio: number
  // boldPrecision over the spans as the model produced them, from the classification this ran;
  // null when it did not classify ('none', 'length'). Scored on the returned content instead, the
  // metric would read 1 whenever any span survives: the rule keeps exactly the spans it credits.
  preCapBoldPrecision: number | null
  // Spans kept only because unwrapping them would have left a user point with no marking at all.
  rescuedSpanCount: number
}

/**
 * Decides which `**...**` spans survive in a USER_GUIDED summary.
 *
 * In `provenanceOnly` mode every span the user's notes do not account for is unwrapped, at any bold
 * ratio, and a span is then restored for each surviving user point that would otherwise carry no
 * bold. Outside that mode the behaviour is the saturation cap unchanged: below
 * SUMMARY_BOLD_SATURATION_RATIO nothing is touched, above it the user's spans are kept and the rest
 * unwrapped, falling back to the length rule when spans cannot be classified.
 *
 * When classification is unavailable in `provenanceOnly` mode this falls back to the cap's
 * behaviour rather than to the length rule. The length rule on unsaturated content would unwrap
 * every span over SUMMARY_BOLD_KEEP_MAX_WORDS words — the user's sentences stripped, the model's
 * short key terms kept, which is the failure the provenance rule exists to prevent.
 */
export function resolveSummaryBold(
  markdown: string,
  userNoteText?: string,
  options?: {provenanceOnly?: boolean}
): SummaryBoldResult {
  const {spans, boldRatio} = measureSummaryBold(markdown)
  const saturated = boldRatio > SUMMARY_BOLD_SATURATION_RATIO
  const provenanceOnly = options?.provenanceOnly === true
  const untouched = (): SummaryBoldResult => ({
    content: markdown,
    capMode: 'none',
    contentChanged: false,
    preCapBoldRatio: boldRatio,
    preCapBoldPrecision: null,
    rescuedSpanCount: 0
  })

  if (!provenanceOnly && !saturated) {
    return untouched()
  }

  const classification = userNoteText === undefined ? null : scoreSpansAgainstNotes(userNoteText, spans)
  // The verdicts pair with the spans by index. Were the counts ever to differ, every span past the
  // drift point would read as the model's and lose its bold silently — so refuse the classification
  // and fall back, like the other cases it cannot be trusted in.
  const scored = classification !== null && classification.spans.length === spans.length ? classification : null
  if (scored === null) {
    if (!saturated) return untouched()
    const content = markdown.replace(SUMMARY_BOLD_SPAN, (match, inner: string) =>
      inner.trim().split(/\s+/).length <= SUMMARY_BOLD_KEEP_MAX_WORDS ? match : inner
    )
    return {
      content,
      capMode: 'length',
      contentChanged: content !== markdown,
      preCapBoldRatio: boldRatio,
      preCapBoldPrecision: null,
      rescuedSpanCount: 0
    }
  }

  const kept = new Set<number>()
  scored.spans.forEach((span, index) => {
    if (span.userDerived) kept.add(index)
  })
  // Only the provenance-only variant rescues: widening it to the saturation path would change what
  // student and educator accounts receive.
  const rescued = provenanceOnly
    ? spansRescuingOrphanedPoints(scored.points, scored.spanWords, kept)
    : new Set<number>()
  for (const index of rescued) kept.add(index)

  // replace() visits spans in the same order match() produced them, so the index lines up.
  let spanIndex = 0
  const content = markdown.replace(SUMMARY_BOLD_SPAN, (match, inner: string) => (kept.has(spanIndex++) ? match : inner))
  return {
    content,
    capMode: 'provenance',
    contentChanged: content !== markdown,
    preCapBoldRatio: boldRatio,
    preCapBoldPrecision: scored.boldPrecision,
    rescuedSpanCount: rescued.size
  }
}
